import fixImageURI from "@/app/ec/product/fixImageURI";
import { ReorderSortType } from "@/app/reorder/reorderSortType";
import { baseApi } from "@/store/baseApi";
import dayjs from "dayjs";
import Cookies from "js-cookie";

/**
 * Shopping cart endpoints against the users service — bare trimmed DTOs, no
 * BasicRestfulResponse envelope. All URLs go through the
 * /rest/users/shopping-cart proxy route handler, which
 * forwards guest identity cookies and relays guest-creation Set-Cookie
 * headers. Transforms are kept thin so contract adjustments stay one-liners.
 */

/** Shared cart-payload → ShoppingCartState transform (also used by the modify mutation). */
export function transformCartPayload(shoppingCart: ShoppingCartPayloadFromServer): ShoppingCartState {
  const shoppingCartProducts = shoppingCart.vendorOrders.flatMap((vo) => {
    return vo.products;
  });
  return {
    subTotal: shoppingCart.subTotal,
    totalQuantity: shoppingCartProducts
      .filter((p) => !p.noLongerForSale)
      .map((p) => p.quantity)
      .reduce((previousValue, currentValue) => {
        return previousValue + currentValue;
      }, 0),
    isMerged: shoppingCart.isMerged ?? false,
    replacedNotInStockWithConsolidation: shoppingCart.replacedNotInStockWithConsolidation ?? false,
    standardShipping: shoppingCart.standardShipping,
    heavyShipping: shoppingCart.heavyShipping,
    retailTotal: shoppingCart.retailTotal,
    savingsOverRetail: shoppingCart.savingsOverRetail,
    grandTotal: shoppingCart.subTotal + shoppingCart.standardShipping + shoppingCart.heavyShipping,

    shoppingCartHash: shoppingCartProducts
      .map((p) => {
        return p.mpId.toString() + p.vendorProductId.toString() + p.quantity.toString() + p.totalInventory.toString();
      })
      .join(""),
    vendorOrders: shoppingCart.vendorOrders.map((vendorOrder) => {
      return {
        ...vendorOrder,
        products: vendorOrder.products.map((product) => {
          const returnableProduct: ShoppingCartVendorOrderProduct = {
            ...product,
            mediaPath: fixImageURI(product.mediaPath),
          };
          if (returnableProduct.nextAutoOrderDate) {
            returnableProduct.nextAutoOrderDate = dayjs(returnableProduct.nextAutoOrderDate).format("MM/DD/YYYY");
          }
          return returnableProduct;
        }),
      };
    }),
    estimatedTax: 0,
    shoppingCartErrors: [],
    shoppingCartSmartMessages: [],
  };
}

/** The users service's empty-cart wire shape — what GET /shopping-cart returns after a clear. */
const EMPTY_CART: ShoppingCartPayloadFromServer = {
  subTotal: 0,
  retailTotal: 0,
  savingsOverRetail: 0,
  standardShipping: 0,
  heavyShipping: 0,
  grandTotal: 0,
  replacedNotInStockWithConsolidation: false,
  vendorOrders: [],
};

export const shoppingCartApi = baseApi
  .enhanceEndpoints({
    addTagTypes: [
      "SHOPPING_CART",
      "SAVE_FOR_LATER",
      "OPTIMAL_SAVINGS",
      "FREE_SHIPPING_SUGGESTIONS",
      "REORDER_SUGGESTIONS",
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getShoppingCart: builder.query<ShoppingCartState, void>({
        query: () => "users/shopping-cart",
        providesTags: () => ["SHOPPING_CART"],
        transformResponse: transformCartPayload,
      }),

      modifyShoppingCart: builder.mutation<ModifyShoppingCartResult, ModifyShoppingCartProps>({
        query: ({ productsToModify, incrementQuantity }) => {
          const gtm_preview = Cookies.get("gtm_preview_header") ?? "";
          return {
            url: "users/shopping-cart/modify" + (incrementQuantity ? "?incrementQuantity=true" : ""),
            method: "POST",
            body: productsToModify,
            headers: {
              gtm_preview_header: gtm_preview,
            },
          };
        },
        // `status` preserves the consumer contract (200 vs 206 partial success)
        transformResponse: (response: ModifyCartResponsePayload, meta) => ({
          status: meta?.response?.status ?? 200,
          shoppingCart: response.shoppingCart ? transformCartPayload(response.shoppingCart) : undefined,
          productsNotModified: response.productsNotModified,
        }),
        // Large-cart perf: the response already carries the fresh cart,
        // so write it into the getShoppingCart cache instead of invalidating the
        // tag — at 250 items the invalidation refetch re-downloaded an identical
        // 264KB payload and triggered a second full-page render storm per change.
        // Missing cart in the response or a failed mutation falls back to
        // invalidation (matching RTK's invalidate-even-on-error default).
        onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
          try {
            const { data } = await queryFulfilled;
            if (data.shoppingCart) {
              dispatch(shoppingCartApi.util.upsertQueryData("getShoppingCart", undefined, data.shoppingCart));
            } else {
              dispatch(shoppingCartApi.util.invalidateTags(["SHOPPING_CART"]));
            }
          } catch {
            dispatch(shoppingCartApi.util.invalidateTags(["SHOPPING_CART"]));
          }
        },
        // Any cart change can alter the optimizer math — refetch savings after every modify
        invalidatesTags: ["OPTIMAL_SAVINGS", "FREE_SHIPPING_SUGGESTIONS"],
      }),

      quickItemAdd: builder.mutation<QuickItemAddResponse, { mpId: number; quantity: number; postalCode?: string }>({
        query: ({ mpId, quantity, postalCode }) => ({
          url: `users/shopping-cart/quick-item-add${postalCode ? `?postalCode=${encodeURIComponent(postalCode)}` : ""}`,
          method: "POST",
          body: { mpId, quantity },
        }),
        invalidatesTags: ["SHOPPING_CART", "OPTIMAL_SAVINGS", "FREE_SHIPPING_SUGGESTIONS"],
      }),

      /**
       * Delete the ENTIRE cart — the /shopping-cart/delete/all Customer-Service
       * action. The cart is definitionally empty after a successful clear, so
       * the empty state is written straight into the cache (same pattern as
       * modify) — the
       * confirmation page can then redirect onto an already-correct cart with
       * no refetch wait and no stale-cart flash.
       */
      clearShoppingCart: builder.mutation<void, void>({
        query: () => ({
          url: "users/shopping-cart",
          method: "DELETE",
        }),
        onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
          try {
            await queryFulfilled;
            dispatch(
              shoppingCartApi.util.upsertQueryData("getShoppingCart", undefined, transformCartPayload(EMPTY_CART)),
            );
          } catch {
            dispatch(shoppingCartApi.util.invalidateTags(["SHOPPING_CART"]));
          }
        },
        invalidatesTags: ["OPTIMAL_SAVINGS", "FREE_SHIPPING_SUGGESTIONS"],
      }),

      getOptimalSavings: builder.query<OptimalSavingsPayload | null, void>({
        query: () => "users/shopping-cart/optimal-savings",
        providesTags: () => ["OPTIMAL_SAVINGS"],
      }),

      consolidateCart: builder.mutation<void, void>({
        query: () => ({
          url: "users/shopping-cart/consolidate",
          method: "POST",
        }),
        invalidatesTags: ["SHOPPING_CART", "OPTIMAL_SAVINGS", "FREE_SHIPPING_SUGGESTIONS"],
      }),

      revertCart: builder.mutation<void, Array<RevertProduct>>({
        query: (savedProducts) => ({
          url: "users/shopping-cart/revert",
          method: "POST",
          body: savedProducts,
        }),
        invalidatesTags: ["SHOPPING_CART", "OPTIMAL_SAVINGS", "FREE_SHIPPING_SUGGESTIONS"],
      }),

      getSaveForLater: builder.query<Array<SaveForLaterProduct>, void>({
        query: () => "users/shopping-cart/save-for-later",
        providesTags: () => ["SAVE_FOR_LATER"],
        // The users service lists oldest-saved first and sends no timestamp or
        // line number, so reversing is the only way to show newest first (SPR-2847).
        transformResponse: (products: Array<SaveForLaterProduct>) =>
          products
            .map((product) => ({
              ...product,
              mediaPath: fixImageURI(product.mediaPath),
            }))
            .reverse(),
      }),

      moveToSaveForLater: builder.mutation<ModifyShoppingCartResult, { vpId: number }>({
        query: ({ vpId }) => ({
          url: `users/shopping-cart/save-for-later/${vpId}`,
          method: "POST",
        }),
        transformResponse: (response: ModifyCartResponsePayload, meta) => ({
          status: meta?.response?.status ?? 200,
          shoppingCart: response.shoppingCart ? transformCartPayload(response.shoppingCart) : undefined,
          productsNotModified: response.productsNotModified,
        }),
        // Cart comes from the response; see modifyShoppingCart's onQueryStarted note.
        onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
          try {
            const { data } = await queryFulfilled;
            if (data.shoppingCart) {
              dispatch(shoppingCartApi.util.upsertQueryData("getShoppingCart", undefined, data.shoppingCart));
            } else {
              dispatch(shoppingCartApi.util.invalidateTags(["SHOPPING_CART"]));
            }
          } catch {
            dispatch(shoppingCartApi.util.invalidateTags(["SHOPPING_CART"]));
          }
        },
        invalidatesTags: ["SAVE_FOR_LATER", "OPTIMAL_SAVINGS", "FREE_SHIPPING_SUGGESTIONS"],
      }),

      moveSaveForLaterToCart: builder.mutation<ModifyShoppingCartResult, { vpId: number }>({
        query: ({ vpId }) => ({
          url: `users/shopping-cart/save-for-later/${vpId}/to-cart`,
          method: "POST",
        }),
        transformResponse: (response: ModifyCartResponsePayload, meta) => ({
          status: meta?.response?.status ?? 200,
          shoppingCart: response.shoppingCart ? transformCartPayload(response.shoppingCart) : undefined,
          productsNotModified: response.productsNotModified,
        }),
        // Cart comes from the response; see modifyShoppingCart's onQueryStarted note.
        onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
          try {
            const { data } = await queryFulfilled;
            if (data.shoppingCart) {
              dispatch(shoppingCartApi.util.upsertQueryData("getShoppingCart", undefined, data.shoppingCart));
            } else {
              dispatch(shoppingCartApi.util.invalidateTags(["SHOPPING_CART"]));
            }
          } catch {
            dispatch(shoppingCartApi.util.invalidateTags(["SHOPPING_CART"]));
          }
        },
        invalidatesTags: ["SAVE_FOR_LATER", "OPTIMAL_SAVINGS", "FREE_SHIPPING_SUGGESTIONS"],
      }),

      deleteSaveForLater: builder.mutation<void, { vpId: number }>({
        query: ({ vpId }) => ({
          url: `users/save-for-later/delete/${vpId}`,
          method: "POST",
        }),
        invalidatesTags: ["SAVE_FOR_LATER"],
      }),

      getReorderSuggestions: builder.query<Array<ShoppingCartReorderProduct>, { postalCode?: string }>({
        query: ({ postalCode }) =>
          `users/shopping-cart/reorder-suggestions${postalCode ? `?postalCode=${encodeURIComponent(postalCode)}` : ""}`,
        providesTags: () => ["REORDER_SUGGESTIONS"],
        transformResponse: (products: Array<ShoppingCartReorderProduct>) =>
          products.map((product) => ({
            ...product,
            mediaPath: product.mediaPath ? fixImageURI(product.mediaPath) : product.mediaPath,
          })),
      }),

      /** The shopper's order history, most purchased first (users reorder sortBy=MOST_PURCHASED, one year back). */
      getMostPurchasedReorders: builder.query<Array<ReorderItem>, { pageSize: number; postalCode?: string }>({
        query: ({ pageSize, postalCode }) => {
          const params = new URLSearchParams({
            sortBy: String(ReorderSortType.MOST_PURCHASED),
            pageSize: String(pageSize),
            // Price and catalog lookups for this page only; totalRows comes back null.
            enrichCurrentPageOnly: "true",
          });
          if (postalCode) params.set("postalCode", postalCode);
          return `users/shopping-cart/reorder?${params.toString()}`;
        },
        providesTags: () => ["REORDER_SUGGESTIONS"],
        // data is null when the shopper has no history.
        transformResponse: (page: Pick<PaginatedReorderData, "data">) =>
          (page.data ?? []).map((item) => ({
            ...item,
            smallImagePath: item.smallImagePath ? fixImageURI(item.smallImagePath) : item.smallImagePath,
          })),
      }),

      getFreeShippingSuggestions: builder.query<Array<FreeShippingProductSuggestion>, void>({
        query: () => "users/shopping-cart/free-shipping-suggestions",
        providesTags: () => ["FREE_SHIPPING_SUGGESTIONS"],
        transformResponse: (products: Array<FreeShippingProductSuggestion>) =>
          products.map((product) => ({
            ...product,
            mediaPath: fixImageURI(product.mediaPath),
          })),
      }),
    }),
  });

export const {
  useGetShoppingCartQuery,
  useLazyGetShoppingCartQuery,
  useModifyShoppingCartMutation,
  useClearShoppingCartMutation,
  useQuickItemAddMutation,
  useGetOptimalSavingsQuery,
  useConsolidateCartMutation,
  useRevertCartMutation,
  useGetSaveForLaterQuery,
  useMoveToSaveForLaterMutation,
  useMoveSaveForLaterToCartMutation,
  useDeleteSaveForLaterMutation,
  useGetReorderSuggestionsQuery,
  useGetMostPurchasedReordersQuery,
  useGetFreeShippingSuggestionsQuery,
  util: { getRunningQueriesThunk },
} = shoppingCartApi;
export const { getShoppingCart, modifyShoppingCart } = shoppingCartApi.endpoints;

export type ModifyShoppingCartProps = {
  productsToModify: Array<ModifyCartProduct>;
  incrementQuantity?: boolean;
};

export type ModifyShoppingCartResult = {
  /** HTTP status (200 full success / 206 partial) — preserves the consumer contract. */
  status: number;
  shoppingCart?: ShoppingCartState;
  productsNotModified?: Array<{
    quantity: number;
    mpId: number;
    vendorProductId: number;
    minimumQuantity: number;
  }>;
};
